import { useState } from 'preact/hooks';
import {
  detectTimerMinutes,
  parseIngredientLine,
  UNITS,
  type IngredientGroup,
  type IngredientItem,
  type RecipeData,
  type RecipeStep,
} from '../../shared/recipe';

type Recipe = RecipeData;
const csv = (a: string[] | undefined) => (a ?? []).join(', ');
const fromCsv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const num = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

function move<T>(arr: T[], from: number, to: number): T[] {
  const a = [...arr];
  const [x] = a.splice(from, 1);
  a.splice(to, 0, x);
  return a;
}

function IngredientGroupEditor({
  group,
  index,
  onChange,
  onRemove,
  recipes,
}: {
  group: IngredientGroup;
  index: number;
  onChange: (g: IngredientGroup) => void;
  onRemove: () => void;
  recipes: { id: string; title: string }[];
}) {
  const [paste, setPaste] = useState('');
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const set = (i: number, patch: Partial<IngredientItem>) => onChange({ ...group, items: group.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const parsePaste = () => {
    const lines = paste.split('\n').map((l) => l.trim()).filter(Boolean);
    onChange({ ...group, items: [...group.items, ...lines.map(parseIngredientLine)] });
    setPaste('');
  };
  const blank = (): IngredientItem => ({ qty: null, qty_max: null, unit: null, item: '', prep: null, note: null, optional: false, scalable: true, linked_card_id: null, raw: '' });

  return (
    <div class="group-box">
      <div class="row" style={{ marginBottom: 'var(--bk-space-2)' }}>
        <input
          class="dr-input bk-input-sm grow"
          aria-label={`Group ${index + 1} heading`}
          placeholder="Group heading (optional), e.g. Dough"
          value={group.name ?? ''}
          onInput={(e) => onChange({ ...group, name: e.currentTarget.value || null })}
        />
        <button type="button" class="dr-btn bk-ghost bk-sm" onClick={onRemove}>
          Remove group
        </button>
      </div>
      <table class="ing-table">
        <thead>
          <tr>
            <th aria-label="Reorder" />
            <th class="col-qty">Qty</th>
            <th class="col-qty">to</th>
            <th class="col-unit">Unit</th>
            <th>Ingredient</th>
            <th>Prep</th>
            <th>Note</th>
            <th>Opt.</th>
            <th>Scale</th>
            <th>Sub-recipe</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {group.items.map((it, i) => (
            <tr
              draggable
              class={dragFrom === i ? 'dragging' : ''}
              onDragStart={() => setDragFrom(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragFrom !== null && dragFrom !== i) onChange({ ...group, items: move(group.items, dragFrom, i) });
                setDragFrom(null);
              }}
              onDragEnd={() => setDragFrom(null)}
            >
              <td class="drag" title="Drag to reorder" aria-hidden="true">
                ⋮⋮
              </td>
              <td>
                <input class="dr-input bk-input-sm" aria-label="Quantity" inputMode="decimal" value={it.qty ?? ''} onChange={(e) => set(i, { qty: num(e.currentTarget.value) })} />
              </td>
              <td>
                <input class="dr-input bk-input-sm" aria-label="Quantity up to" inputMode="decimal" value={it.qty_max ?? ''} onChange={(e) => set(i, { qty_max: num(e.currentTarget.value) })} />
              </td>
              <td>
                <select class="dr-input bk-input-sm" aria-label="Unit" value={it.unit ?? ''} onChange={(e) => set(i, { unit: e.currentTarget.value || null })}>
                  <option value="">—</option>
                  {UNITS.map((u) => (
                    <option value={u}>{u.replace('_', ' ')}</option>
                  ))}
                  {it.unit && !(UNITS as readonly string[]).includes(it.unit) && <option value={it.unit}>{it.unit} (unknown)</option>}
                </select>
              </td>
              <td>
                <input class="dr-input bk-input-sm" aria-label="Ingredient" value={it.item} onInput={(e) => set(i, { item: e.currentTarget.value })} required />
              </td>
              <td>
                <input class="dr-input bk-input-sm" aria-label="Preparation" value={it.prep ?? ''} onInput={(e) => set(i, { prep: e.currentTarget.value || null })} />
              </td>
              <td>
                <input class="dr-input bk-input-sm" aria-label="Note" value={it.note ?? ''} onInput={(e) => set(i, { note: e.currentTarget.value || null })} />
              </td>
              <td>
                <input type="checkbox" aria-label="Optional" checked={it.optional} onChange={(e) => set(i, { optional: e.currentTarget.checked })} />
              </td>
              <td>
                <input type="checkbox" aria-label="Scalable" checked={it.scalable} onChange={(e) => set(i, { scalable: e.currentTarget.checked })} />
              </td>
              <td>
                <select class="dr-input bk-input-sm" aria-label="Linked sub-recipe" value={it.linked_card_id ?? ''} onChange={(e) => set(i, { linked_card_id: e.currentTarget.value || null })}>
                  <option value="">—</option>
                  {recipes.map((r) => (
                    <option value={r.id}>{r.title}</option>
                  ))}
                </select>
              </td>
              <td class="row" style={{ flexWrap: 'nowrap' }}>
                <button type="button" class="dr-btn bk-ghost bk-sm" aria-label="Move up" disabled={i === 0} onClick={() => onChange({ ...group, items: move(group.items, i, i - 1) })}>
                  ↑
                </button>
                <button type="button" class="dr-btn bk-ghost bk-sm" aria-label="Move down" disabled={i === group.items.length - 1} onClick={() => onChange({ ...group, items: move(group.items, i, i + 1) })}>
                  ↓
                </button>
                <button type="button" class="dr-btn bk-ghost bk-sm" aria-label={`Remove ${it.item || 'row'}`} onClick={() => onChange({ ...group, items: group.items.filter((_, j) => j !== i) })}>
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="row" style={{ marginTop: 'var(--bk-space-2)' }}>
        <button type="button" class="dr-btn bk-sm" onClick={() => onChange({ ...group, items: [...group.items, blank()] })}>
          + Row
        </button>
      </div>
      <details style={{ marginTop: 'var(--bk-space-2)' }}>
        <summary class="small muted">Paste a block of ingredient lines</summary>
        <textarea class="dr-input" aria-label="Paste ingredient lines" placeholder={'2 tbsp olive oil\n3 leeks, sliced\nSalt to taste'} value={paste} onInput={(e) => setPaste(e.currentTarget.value)} />
        <button type="button" class="dr-btn bk-sm" onClick={parsePaste} disabled={!paste.trim()}>
          Parse into rows
        </button>
      </details>
    </div>
  );
}

export function RecipeForm({ data, onChange, recipes }: { data: Recipe; onChange: (d: Recipe) => void; recipes: { id: string; title: string }[] }) {
  const set = (patch: Partial<Recipe>) => onChange({ ...data, ...patch });
  const setTimes = (k: keyof Recipe['times'], v: string) => set({ times: { ...data.times, [k]: num(v) } });
  const [stepPaste, setStepPaste] = useState('');
  const steps = data.steps ?? [];
  const setStep = (i: number, patch: Partial<RecipeStep>) => set({ steps: steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const renumber = (s: RecipeStep[]) => s.map((x, i) => ({ ...x, n: i + 1 }));

  return (
    <div class="stack">
      <fieldset class="panel">
        <legend>Basics</legend>
        <div class="field-grid">
          <div>
            <label for="sv">Makes</label>
            <input id="sv" class="dr-input" inputMode="decimal" value={data.servings?.amount ?? ''} onChange={(e) => {
              const n = num(e.currentTarget.value);
              set({ servings: n ? { amount: n, unit: data.servings?.unit ?? 'servings' } : null });
            }} />
          </div>
          <div>
            <label for="su">Unit</label>
            <input id="su" class="dr-input" placeholder="servings, loaf, pancakes" value={data.servings?.unit ?? 'servings'} onInput={(e) => set({ servings: { amount: data.servings?.amount ?? 1, unit: e.currentTarget.value || 'servings' } })} />
          </div>
          <div>
            <label for="tp">Prep (min)</label>
            <input id="tp" class="dr-input" inputMode="numeric" value={data.times?.prep_min ?? ''} onChange={(e) => setTimes('prep_min', e.currentTarget.value)} />
          </div>
          <div>
            <label for="tc">Bake (min)</label>
            <input id="tc" class="dr-input" inputMode="numeric" value={data.times?.cook_min ?? ''} onChange={(e) => setTimes('cook_min', e.currentTarget.value)} />
          </div>
          <div>
            <label for="tr">Rest (min)</label>
            <input id="tr" class="dr-input" inputMode="numeric" value={data.times?.rest_min ?? ''} onChange={(e) => setTimes('rest_min', e.currentTarget.value)} />
          </div>
          <div>
            <label for="tt">Total (min)</label>
            <input id="tt" class="dr-input" inputMode="numeric" placeholder="auto" value={data.times?.total_min ?? ''} onChange={(e) => setTimes('total_min', e.currentTarget.value)} />
          </div>
          <div>
            <label for="df">Difficulty</label>
            <select id="df" class="dr-input" value={data.difficulty ?? ''} onChange={(e) => set({ difficulty: (e.currentTarget.value || null) as Recipe['difficulty'] })}>
              <option value="">—</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>
        <div class="field-grid" style={{ marginTop: 'var(--bk-space-3)' }}>
          {(['cuisine', 'course', 'diet', 'season', 'equipment'] as const).map((k) => (
            <div>
              <label for={`f-${k}`}>{k[0].toUpperCase() + k.slice(1)}</label>
              <input id={`f-${k}`} class="dr-input" placeholder="comma separated" value={csv(data[k])} onChange={(e) => set({ [k]: fromCsv(e.currentTarget.value) } as Partial<Recipe>)} />
            </div>
          ))}
        </div>
        <p class="muted small">Cuisine, course, diet and season also become tags.</p>
      </fieldset>

      <fieldset class="panel">
        <legend>Ingredients</legend>
        {(data.ingredient_groups ?? []).map((g, gi) => (
          <IngredientGroupEditor
            group={g}
            index={gi}
            recipes={recipes}
            onChange={(ng) => set({ ingredient_groups: data.ingredient_groups.map((x, j) => (j === gi ? ng : x)) })}
            onRemove={() => set({ ingredient_groups: data.ingredient_groups.filter((_, j) => j !== gi) })}
          />
        ))}
        <button type="button" class="dr-btn bk-sm" onClick={() => set({ ingredient_groups: [...(data.ingredient_groups ?? []), { name: null, items: [] }] })}>
          + Group
        </button>
      </fieldset>

      <fieldset class="panel">
        <legend>Method</legend>
        {steps.map((s, i) => (
          <div class="step-row">
            <span class="muted" aria-hidden="true">{i + 1}.</span>
            <textarea
              class="dr-input"
              aria-label={`Step ${i + 1}`}
              style={{ minHeight: 'var(--touch-cook)' }}
              value={s.text}
              onInput={(e) => setStep(i, { text: e.currentTarget.value })}
              onBlur={(e) => s.timer_min == null && setStep(i, { timer_min: detectTimerMinutes(e.currentTarget.value) })}
            />
            <input class="dr-input bk-input-sm" aria-label={`Timer for step ${i + 1} (minutes)`} placeholder="⏱ min" inputMode="numeric" value={s.timer_min ?? ''} onChange={(e) => setStep(i, { timer_min: num(e.currentTarget.value) })} />
            <div class="row" style={{ flexWrap: 'nowrap' }}>
              <button type="button" class="dr-btn bk-ghost bk-sm" aria-label="Move step up" disabled={i === 0} onClick={() => set({ steps: renumber(move(steps, i, i - 1)) })}>
                ↑
              </button>
              <button type="button" class="dr-btn bk-ghost bk-sm" aria-label="Move step down" disabled={i === steps.length - 1} onClick={() => set({ steps: renumber(move(steps, i, i + 1)) })}>
                ↓
              </button>
              <button type="button" class="dr-btn bk-ghost bk-sm" aria-label={`Remove step ${i + 1}`} onClick={() => set({ steps: renumber(steps.filter((_, j) => j !== i)) })}>
                ✕
              </button>
            </div>
          </div>
        ))}
        <div class="row">
          <button type="button" class="dr-btn bk-sm" onClick={() => set({ steps: [...steps, { n: steps.length + 1, text: '', timer_min: null, media_id: null }] })}>
            + Step
          </button>
        </div>
        <details style={{ marginTop: 'var(--bk-space-2)' }}>
          <summary class="small muted">Paste steps (one per line or paragraph)</summary>
          <textarea class="dr-input" aria-label="Paste steps" value={stepPaste} onInput={(e) => setStepPaste(e.currentTarget.value)} />
          <button
            type="button"
            class="dr-btn bk-sm"
            disabled={!stepPaste.trim()}
            onClick={() => {
              const lines = stepPaste.split(/\n+/).map((l) => l.trim().replace(/^\d+[.)]\s*/, '')).filter(Boolean);
              set({ steps: renumber([...steps, ...lines.map((text) => ({ n: 0, text, timer_min: detectTimerMinutes(text), media_id: null }))]) });
              setStepPaste('');
            }}
          >
            Add steps
          </button>
        </details>
        <div class="field" style={{ marginTop: 'var(--bk-space-4)' }}>
          <label for="yn">Yield / storage note</label>
          <input id="yn" class="dr-input" placeholder="Freezes well for 3 months" value={data.yield_note ?? ''} onInput={(e) => set({ yield_note: e.currentTarget.value || null })} />
        </div>
      </fieldset>
    </div>
  );
}
